import * as React from 'react';
import { useCallback, useEffect, useId, useRef } from 'react';
import { wrapper, labelCls, field, helperText, skeleton as skeletonCls } from './Textarea.css';

export type TextareaSize = 'sm' | 'md' | 'lg';

export interface TextareaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'size'
> {
  label?: string;
  helper?: string;
  /**
   * Error caption — its presence drives the alarm border + `aria-invalid`.
   * Uniform across the form family; there is no separate `invalid?` boolean
   * (L26).
   */
  error?: string;
  size?: TextareaSize;
  /** Automatically resize to fit content (up to maxRows). */
  autoResize?: boolean;
  maxRows?: number;
  skeleton?: boolean;
  /** Stretch to fill the parent. Use this prop, not `style={{ width: '100%' }}`. */
  fullWidth?: boolean;
  /** React 19 ref-as-prop (replaces the old forwardRef pattern). */
  ref?: React.Ref<HTMLTextAreaElement>;
}

/**
 * Textarea — multiline text field. Like `<Input>`, supports label,
 * helper, error caption, and skeleton loading state. Set `autoResize`
 * to grow with content up to `maxRows` (default 12) — beyond that, the
 * textarea internal-scrolls. For chat-style auto-grow with a tighter
 * cap and a visible action row, use `<Composer>` instead.
 */
export function Textarea({
  label,
  helper,
  error,
  size = 'md',
  autoResize = false,
  maxRows = 12,
  skeleton = false,
  fullWidth = false,
  id,
  style,
  onInput,
  ref,
  ...rest
}: TextareaProps) {
  const autoId = useId();
  const textareaId = id ?? autoId;
  const helperId = `${textareaId}-helper`;
  const localRef = useRef<HTMLTextAreaElement | null>(null);

  // Merge refs — preserve the legacy callback/ref-object support.
  const setRefs = useCallback(
    (el: HTMLTextAreaElement | null) => {
      localRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as { current: HTMLTextAreaElement | null }).current = el;
    },
    [ref],
  );

  const resize = useCallback(() => {
    if (!autoResize || !localRef.current) return;
    const el = localRef.current;
    el.style.height = 'auto';
    const lineHeight = parseInt(getComputedStyle(el).lineHeight, 10) || 22;
    const max = lineHeight * maxRows;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [autoResize, maxRows]);

  useEffect(() => {
    resize();
  }, [resize, rest.value, rest.defaultValue]);

  const handleInput: NonNullable<React.TextareaHTMLAttributes<HTMLTextAreaElement>['onInput']> = (
    e,
  ) => {
    resize();
    onInput?.(e);
  };

  if (skeleton) {
    return (
      <div className={wrapper({ fullWidth })} style={style}>
        {label && (
          <span className={labelCls} aria-hidden="true">
            {label}
          </span>
        )}
        <div className={skeletonCls({ size })} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={wrapper({ fullWidth })} style={style}>
      {label && (
        <label className={labelCls} htmlFor={textareaId}>
          {label}
        </label>
      )}
      <textarea
        ref={setRefs}
        id={textareaId}
        className={field({ size, error: Boolean(error) })}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={helper || error ? helperId : undefined}
        onInput={handleInput}
        {...rest}
      />
      {(helper || error) && (
        <span id={helperId} className={helperText({ error: Boolean(error) })}>
          {error ?? helper}
        </span>
      )}
    </div>
  );
}
Textarea.displayName = 'Textarea';
